// Offline write outbox — IndexedDB queue that flushes to /api/sync-outbox/push
// when the browser comes back online (or when the next manual flush
// runs). Designed to be opt-in: any page that wants offline-write
// support imports `enqueue()` instead of calling fetch directly, then
// the page's "saved" state shows immediately while the network is
// down. When online, this lib still writes through (so it's safe to
// use as a default).
//
// Idempotency: every enqueue gets a UUID `client_op_id`. Server stores
// the result keyed by that id, so a network blip mid-flush replays
// safely.

const DB_NAME = "fk_offline";
const DB_VERSION = 1;
const STORE = "outbox";

interface QueueRow {
  client_op_id: string;
  kind: string;
  payload: unknown;
  queued_at: number;
  retries: number;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: "client_op_id" });
        store.createIndex("queued_at", "queued_at");
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function tx<T>(
  db: IDBDatabase,
  mode: IDBTransactionMode,
  fn: (s: IDBObjectStore) => IDBRequest<T> | T,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const store = tx.objectStore(STORE);
    const out = fn(store);
    if (out instanceof IDBRequest) {
      out.onsuccess = () => resolve(out.result);
      out.onerror = () => reject(out.error);
    } else {
      tx.oncomplete = () => resolve(out as T);
      tx.onerror = () => reject(tx.error);
    }
  });
}

function uuid(): string {
  // crypto.randomUUID is available in all modern browsers + Workers.
  return crypto.randomUUID();
}

// Add an op to the queue + immediately try to flush. If online, the
// write hits the server right away. If offline, it stays queued until
// the next online event.
export async function enqueue<T = unknown>(
  kind: string,
  payload: T,
): Promise<{ queued: true; client_op_id: string }> {
  const db = await openDb();
  const row: QueueRow = {
    client_op_id: uuid(),
    kind,
    payload,
    queued_at: Date.now(),
    retries: 0,
  };
  await tx(db, "readwrite", (s) => s.put(row));
  // Fire-and-forget flush. Errors swallowed; reconciliation happens on
  // the next online event or manual call.
  void flush();
  return { queued: true, client_op_id: row.client_op_id };
}

export async function flush(): Promise<{ pushed: number; failed: number }> {
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return { pushed: 0, failed: 0 };
  }
  const db = await openDb();
  const rows = await tx<QueueRow[]>(
    db,
    "readonly",
    (s) => s.getAll() as IDBRequest<QueueRow[]>,
  );
  if (rows.length === 0) return { pushed: 0, failed: 0 };
  // Chunk: server caps at 200 ops/request.
  const chunks: QueueRow[][] = [];
  for (let i = 0; i < rows.length; i += 100) chunks.push(rows.slice(i, i + 100));

  let pushed = 0;
  let failed = 0;
  for (const chunk of chunks) {
    try {
      const res = await fetch("/api/sync-outbox/push", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          ops: chunk.map((r) => ({
            client_op_id: r.client_op_id,
            kind: r.kind,
            payload: r.payload,
          })),
        }),
      });
      if (!res.ok) {
        failed += chunk.length;
        continue;
      }
      const data = (await res.json()) as {
        results: Array<{ client_op_id: string; ok: boolean }>;
      };
      // Remove successful ops; leave failures to retry.
      for (const r of data.results) {
        if (r.ok) {
          await tx(db, "readwrite", (s) => s.delete(r.client_op_id));
          pushed++;
        } else {
          failed++;
        }
      }
    } catch {
      failed += chunk.length;
    }
  }
  return { pushed, failed };
}

// Hook up the browser-online listener. Call once at app boot.
let installed = false;
export function installOnlineListener(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;
  window.addEventListener("online", () => {
    void flush();
  });
  // Initial sync attempt — covers the "I just loaded the app and have
  // queued ops from a prior session" case.
  void flush();
}

export async function pendingCount(): Promise<number> {
  try {
    const db = await openDb();
    return tx(db, "readonly", (s) => s.count() as IDBRequest<number>);
  } catch {
    return 0;
  }
}
